/** The cache for faster O(1) lookup. */
const cache = {};
/** The mappings between the different casings. */
const mappings = {
    email: {
        pascal: 'Email',
        truncate: 'email',
    },
    contact_form: {
        pascal: 'ContactForm',
        truncate: 'contactform',
    },
    widget_chat: {
        pascal: 'WidgetChat',
        truncate: 'widgetchat',
    },
    messenger: {
        pascal: 'Messenger',
        truncate: 'messenger',
    },
    facebook_messenger: {
        pascal: 'FacebookMessenger',
        truncate: 'facebookmessenger',
    },
    whats_app: {
        pascal: 'WhatsApp',
        truncate: 'whatsapp',
    },
    pstn_phone: {
        pascal: 'PstnPhone',
        truncate: 'pstnphone',
    },
    callback: {
        pascal: 'Callback',
        truncate: 'callback',
    },
    voicemail: {
        pascal: 'Voicemail',
        truncate: 'voicemail',
    },
    sms: {
        pascal: 'Sms',
        truncate: 'sms',
    },
    twitter: {
        pascal: 'Twitter',
        truncate: 'twitter',
    },
    adachat: {
        pascal: 'AdaChat',
        truncate: 'adachat',
    },
    digitalgeniuschat: {
        pascal: 'DigitalGeniusChat',
        truncate: 'digitalgeniuschat',
    },
    kindlychat: {
        pascal: 'KindlyChat',
        truncate: 'kindlychat',
    },
    genericapimessaging: {
        pascal: 'GenericApiMessaging',
        truncate: 'genericapimessaging',
    },
    trustpilotreviews: {
        pascal: 'TrustpilotReviews',
        truncate: 'trustpilotreviews',
    },
    yotporeviews: {
        pascal: 'YotpoReviews',
        truncate: 'yotporeviews',
    },
};
/**
 * Converts the conversation channel in one of the arbitrary supported casings
 * to a particular one.
 *
 * @param channel The conversation channel.
 * @param to The casing to convert to.
 */
function convertChannelCasing(channel, to) {
    const cacheKey = `${channel}:${to}`;
    if (cache[cacheKey]) {
        return cache[cacheKey];
    }
    const mapping = mappings[channel];
    if (mapping) {
        return to === 'snake' ? channel : mapping[to];
    }
    const snakeCase = Object.keys(mappings).find((mappingKey) => [mappings[mappingKey].pascal, mappings[mappingKey].truncate].includes(channel));
    const result = to === 'snake' ? snakeCase : mappings[snakeCase ?? '']?.[to] || channel;
    cache[cacheKey] = result;
    return result;
}

/** The default offset for paginated requests. */
const defaultPaginatedOffset = 0;
/** The default limit for paginated requests. */
const defaultPaginatedLimit = 25;
/**
 * Creates the endpoint options from a paginated api arg. This adds all the
 * common paginated api arguments as query parameters and through a callback
 * lets the consumer add the remaining arguments that are specific to that
 * endpoint.
 *
 * @param arg The paginated api argument.
 * @param endpointOptionsFn A function that produces the endpoint options.
 * @param options The options to tweak the endpoint options.
 */
function paginatedQuery(arg, endpointOptionsFn, options = {}) {
    if (typeof arg === 'string') {
        return arg;
    }
    const { limit, offset, sort, searchTerm, ...others } = arg;
    const endpointOptions = endpointOptionsFn(others);
    return {
        ...endpointOptions,
        params: {
            ...(endpointOptions.params || {}),
            ...getSupportedPaginationParam({ limit, offset }, options),
            ...getSupportedSearchTermParam({ searchTerm }, options),
            ...getSupportedSortParam({ sort }, { searchTerm }, options),
        },
    };
}
/**
 * Gets the paginated fields that correspond to the endpoint naming convention.
 *
 * @param paginatedApiArgs The paginated api args.
 * @param options The paginated query options to tweak the endpoint options.
 */
function getSupportedPaginationParam({ limit, offset }, { pagination: paginationOptions = {} }) {
    switch (paginationOptions.paramNaming) {
        case 'pageSizePage':
            return {
                ...(typeof limit !== 'undefined'
                    ? { pageSize: limit }
                    : {
                        pageSize: paginationOptions.defaultLimit ?? defaultPaginatedLimit,
                    }),
                ...(typeof offset !== 'undefined'
                    ? {
                        page: offset /
                            (limit ??
                                paginationOptions.defaultLimit ??
                                defaultPaginatedLimit),
                    }
                    : {}),
            };
        case 'pageSizePageNumber':
            return {
                ...(typeof limit !== 'undefined'
                    ? { page_size: limit }
                    : { page_size: defaultPaginatedLimit }),
                ...(typeof offset !== 'undefined'
                    ? {
                        page_number: offset /
                            (limit ??
                                paginationOptions.defaultLimit ??
                                defaultPaginatedLimit),
                    }
                    : {}),
            };
        case 'limitPage':
            return {
                ...(typeof limit !== 'undefined'
                    ? { limit }
                    : { limit: paginationOptions.defaultLimit ?? defaultPaginatedLimit }),
                ...(typeof offset !== 'undefined'
                    ? {
                        page: offset /
                            (limit ??
                                paginationOptions.defaultLimit ??
                                defaultPaginatedLimit),
                    }
                    : {}),
            };
        case 'limitCount':
            return {
                ...(typeof limit !== 'undefined'
                    ? { limit }
                    : { limit: paginationOptions.defaultLimit ?? defaultPaginatedLimit }),
                ...(typeof offset !== 'undefined' ? { count: offset } : {}),
            };
        case 'limitOffset':
        default:
            return {
                ...(typeof limit !== 'undefined'
                    ? { limit }
                    : { limit: paginationOptions.defaultLimit ?? defaultPaginatedLimit }),
                ...(typeof offset !== 'undefined' ? { offset } : {}),
            };
    }
}
/**
 * Gets the search field that corresponds to the endpoint naming convention.
 *
 * @param searchedApiArgs The searched api args.
 * @param options The paginated query options to tweak the endpoint options.
 */
function getSupportedSearchTermParam({ searchTerm }, { search: searchOptions = {} }) {
    const value = searchTerm ?? searchOptions.default;
    return typeof value !== 'undefined'
        ? { [searchOptions.paramName ?? 'search']: value }
        : {};
}
/**
 * Gets the sort field that corresponds to the endpoint naming convention.
 *
 * @param sortedApiArgs The sorted api args.
 * @param searchedApiArgs The sorted api args.
 * @param options The paginated query options to tweak the endpoint options.
 */
function getSupportedSortParam({ sort: sortArg }, { searchTerm }, { sort: sortOptions = {}, search: searchOptions = {} }) {
    const sort = sortArg ?? sortOptions.default;
    let value;
    if (isSortArgArray(sort)) {
        value = sort
            .map(({ key, direction }) => `${key}:${direction || 'asc'}`)
            .join(',');
    }
    else if (sort) {
        value = `${sort.key}:${sort.direction || 'asc'}`;
    }
    return typeof value !== 'undefined' &&
        ((searchOptions.supportsSortedSearch && searchTerm) || !searchTerm)
        ? { [sortOptions.paramName ?? 'sort_by']: value }
        : {};
}
/**
 * Gets the paginated api arg from one of the supported pagination params.
 *
 * @param param The supported pagination param.
 */
function getPaginatedApiParams(param) {
    if (isPageSizePage(param)) {
        const { pageSize, page } = param;
        return {
            ...(typeof pageSize !== 'undefined' ? { limit: pageSize } : {}),
            ...(typeof pageSize !== 'undefined' && typeof page !== 'undefined'
                ? { offset: pageSize * page }
                : {}),
        };
    }
    if (isPageSizePageNumber(param)) {
        const { page_size, page_number } = param;
        return {
            ...(typeof page_size !== 'undefined' ? { limit: page_size } : {}),
            ...(typeof page_size !== 'undefined' && typeof page_number !== 'undefined'
                ? { offset: page_size * page_number }
                : {}),
        };
    }
    if (isLimitPage(param)) {
        const { limit, page } = param;
        return {
            ...(typeof limit !== 'undefined' ? { limit } : {}),
            ...(typeof page !== 'undefined' && typeof limit !== 'undefined'
                ? { offset: page * limit }
                : {}),
        };
    }
    if (isLimitCount(param)) {
        const { limit, count } = param;
        return {
            ...(typeof limit !== 'undefined' ? { limit } : {}),
            ...(typeof count !== 'undefined' ? { offset: count } : {}),
        };
    }
    return { limit: param.limit, offset: param.offset };
}
/**
 * Checks whether the param conforms the limit/count convention.
 *
 * @param param The supported pagination param.
 */
function isLimitCount(param) {
    return (param.limit !== undefined &&
        param.count !== undefined);
}
/**
 * Checks whether the param conforms the limit/page convention.
 *
 * @param param The supported pagination param.
 */
function isLimitPage(param) {
    return (param.limit !== undefined &&
        param.page !== undefined);
}
/**
 * Checks whether the param conforms the page_size/page_umber convention.
 *
 * @param param The supported pagination param.
 */
function isPageSizePageNumber(param) {
    return (param.page_size !==
        undefined &&
        param.page_number !==
            undefined);
}
/**
 * Checks whether the param conforms the pageSize/page convention.
 *
 * @param param The supported pagination param.
 */
function isPageSizePage(param) {
    return (param.pageSize !==
        undefined &&
        param.page !== undefined);
}
/**
 * Checks whether the sort arg is an array.
 *
 * @param sort The sort.
 */
function isSortArgArray(sort) {
    return sort ? Array.isArray(sort) : false;
}

let streamCount = 0;
const getStreamCount = () => {
    return streamCount;
};
const incrementStreamCount = () => {
    streamCount += 1;
};

const streamReader = async ({ url, body, headers = {}, signal, isCancelled, onStart, onChunk, onCancelled, onDone, onError, }) => {
    try {
        const response = await fetch(url, {
            method: 'POST',
            headers: {
                ...headers,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(body),
            signal,
        });
        if (!response.ok) {
            onError(response);
            return;
        }
        const reader = response.body?.getReader();
        if (!reader) {
            onError(response);
            return;
        }
        onStart?.();
        const decoder = new TextDecoder();
        while (true) {
            if (isCancelled?.()) {
                onCancelled?.();
                return;
            }
            const { done, value } = await reader.read();
            if (done) {
                onDone(response);
                return;
            }
            if (value) {
                onChunk(decoder.decode(value, { stream: true }));
            }
        }
    }
    catch (err) {
        if (err.name !== 'AbortError') {
            onError();
        }
    }
};

const streamUpdates = async (updateCachedData, requestConfig, processChunk) => {
    const { url, body, headers } = requestConfig;
    incrementStreamCount();
    const streamCountInitial = getStreamCount();
    await streamReader({
        url,
        body,
        headers,
        isCancelled: () => streamCountInitial !== getStreamCount(),
        onStart: () => {
            updateCachedData((draft) => {
                draft.meta.isLoading = false;
                draft.meta.isStreaming = true;
            });
        },
        onChunk: (chunk) => {
            updateCachedData((draft) => {
                if (processChunk) {
                    processChunk(draft, chunk);
                }
                else {
                    draft.text += chunk;
                }
            });
        },
        onCancelled: () => {
            updateCachedData((draft) => {
                draft.meta.isLoading = true;
                draft.meta.isStreaming = false;
                draft.text = '';
            });
        },
        onDone: (response) => {
            updateCachedData((draft) => {
                draft.meta.isStreaming = false;
                draft.meta.isSuccess = true;
                draft.feedback_id = response.headers.get('dixa-context-id') ?? '';
            });
        },
        onError: (response) => {
            updateCachedData((draft) => {
                draft.meta.isLoading = false;
                draft.meta.isError = true;
                if (response) {
                    draft.feedback_id = response.headers.get('dixa-context-id') ?? '';
                }
            });
        },
    });
};

export { convertChannelCasing, defaultPaginatedLimit, defaultPaginatedOffset, getPaginatedApiParams, getStreamCount, incrementStreamCount, isSortArgArray, paginatedQuery, streamReader, streamUpdates };
//# sourceMappingURL=index.es.js.map
